import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { PERMISSIONS } from '@core/auth/permissions';
import { authGuard } from '@core/guards/auth.guard';
import { permissionGuard } from '@core/guards/permission.guard';
import { environment } from '@env/environment';
import { esSubmoduloRealDeCompras } from '@domains/compras/compras-real.match';
import { esSubmoduloRealDeTesoreria } from '@domains/tesoreria/tesoreria-real.match';

const devGuard = () => {
  if (environment.showStyleguide) return true;
  return inject(Router).createUrlTree(['/dashboard']);
};

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },

  // ── Auth shell (sin autenticación) ──────────────────────────────────────
  {
    path: 'auth',
    loadComponent: () =>
      import('@shared/components/layout/auth-shell/auth-shell.component').then(
        (m) => m.AuthShellComponent,
      ),
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('@domains/auth/feature/login/login.component').then((m) => m.LoginComponent),
        title: 'Iniciar sesión — TCG Factory ERP',
      },
      {
        path: 'auto-login',
        loadComponent: () =>
          import('@domains/auth/feature/auto-login/auto-login.component').then(
            (m) => m.AutoLoginComponent,
          ),
      },
      { path: '', pathMatch: 'full', redirectTo: 'login' },
    ],
  },

  // ── Mudanza · pantallas de tablet (autenticadas, FUERA del app-shell) ───
  // El operario trabaja en el pasillo con una mano: la pantalla va entera para
  // el contenido, sin sidebar ni topbar (precedente: `/styleguide`). Las URL
  // siguen colgando de `/almacen/mudanza/*` por coherencia con el sub-hub, pero
  // las rutas son de primer nivel y se declaran ANTES del shell, que casa con
  // `path: ''` y se quedaría con ellas. El marco visual común lo pone
  // `MudanzaTabletLayoutComponent`, no un shell de router.
  {
    path: 'almacen/mudanza/vaciado',
    canActivate: [authGuard, permissionGuard],
    data: { permission: PERMISSIONS.almacen.mudanzaRead.key },
    loadComponent: () =>
      import('@domains/mudanza/feature/vaciado/mudanza-vaciado-page.component').then(
        (m) => m.MudanzaVaciadoPageComponent,
      ),
    title: 'Vaciado Plaza — Mudanza — TCG Factory ERP',
  },
  {
    path: 'almacen/mudanza/ubicacion',
    canActivate: [authGuard, permissionGuard],
    data: { permission: PERMISSIONS.almacen.mudanzaRead.key },
    loadComponent: () =>
      import('@domains/mudanza/feature/ubicacion/mudanza-ubicacion-page.component').then(
        (m) => m.MudanzaUbicacionPageComponent,
      ),
    title: 'Ubicar Muela — Mudanza — TCG Factory ERP',
  },
  {
    path: 'almacen/mudanza/vaciado-paletizado',
    canActivate: [authGuard, permissionGuard],
    data: { permission: PERMISSIONS.almacen.mudanzaRead.key },
    loadComponent: () =>
      import(
        '@domains/mudanza/feature/paletizado/mudanza-vaciado-paletizado-page.component'
      ).then((m) => m.MudanzaVaciadoPaletizadoPageComponent),
    title: 'Vaciado paletizado Plaza — Mudanza — TCG Factory ERP',
  },
  {
    path: 'almacen/mudanza/ubicar-paletizado',
    canActivate: [authGuard, permissionGuard],
    data: { permission: PERMISSIONS.almacen.mudanzaRead.key },
    loadComponent: () =>
      import(
        '@domains/mudanza/feature/paletizado/mudanza-ubicar-paletizado-page.component'
      ).then((m) => m.MudanzaUbicarPaletizadoPageComponent),
    title: 'Ubicar repo Muela — Mudanza — TCG Factory ERP',
  },

  // Aquí vivía `/avisos/conversacion/:idAviso`, la conversación sacada a su propia pestaña o ventana.
  // Se retiró con el lateral de conversaciones, que persiste al navegar y resuelve lo mismo sin abrir
  // otra superficie. Una dirección vieja cae en el comodín como cualquier ruta que no existe.

  // ── App shell (autenticado) ─────────────────────────────────────────────
  {
    path: '',
    loadComponent: () =>
      import('@shared/components/layout/app-shell/app-shell.component').then(
        (m) => m.AppShellComponent,
      ),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        data: { breadcrumb: 'Dashboard' },
        loadComponent: () =>
          import('@domains/dashboard/feature/dashboard-home/dashboard-home.component').then(
            (m) => m.DashboardHomeComponent,
          ),
        title: 'Dashboard — TCG Factory ERP',
      },
      {
        path: 'reservas',
        data: { breadcrumb: 'Reservas' },
        loadChildren: () =>
          import('@domains/reservas/reservas.routes').then((m) => m.reservasRoutes),
      },
      {
        path: 'clientes',
        data: { breadcrumb: 'Clientes' },
        loadChildren: () =>
          import('@domains/clientes/clientes.routes').then((m) => m.clientesRoutes),
      },
      {
        path: 'juego-organizado',
        data: { breadcrumb: 'Juego Organizado' },
        loadChildren: () =>
          import('@domains/juego-organizado/juego-organizado.routes').then(
            (m) => m.juegoOrganizadoRoutes,
          ),
      },
      {
        path: 'proveedores',
        data: { breadcrumb: 'Proveedores' },
        loadChildren: () =>
          import('@domains/proveedores/proveedores.routes').then((m) => m.proveedoresRoutes),
      },
      {
        path: 'tareas',
        data: { breadcrumb: 'Tareas' },
        loadChildren: () => import('@domains/tareas/tareas.routes').then((m) => m.tareasRoutes),
      },
      // `avisos` ya no existe COMO SECCIÓN: el buzón entero vive dentro del centro de notificaciones,
      // el panel que cuelga de la campana. No hay pantalla a la que navegar, así que tampoco ruta que
      // registrar aquí dentro. Las conversaciones tampoco tienen pantalla: viven en su lateral.
      {
        path: 'comunicaciones',
        data: { breadcrumb: 'Comunicaciones' },
        loadChildren: () =>
          import('@domains/comunicaciones/comunicaciones.routes').then(
            (m) => m.comunicacionesRoutes,
          ),
      },
      {
        path: 'atencion-cliente',
        data: { breadcrumb: 'Atención al cliente' },
        loadChildren: () =>
          import('@domains/atencion-cliente/atencion-cliente.routes').then(
            (m) => m.atencionClienteRoutes,
          ),
      },
      {
        // Dashboard del bucle de mejora. Hermana agregada de la Auditoría: aquí el diagnóstico,
        // allí el detalle fila a fila. Debe ir ANTES de 'sistema/borrador-ia': Angular casa por
        // orden y esta ruta ('.../dashboard') es la más específica de las dos.
        path: 'sistema/borrador-ia/dashboard',
        canActivate: [authGuard, permissionGuard],
        data: {
          breadcrumb: 'Dashboard Borrador IA',
          permission: [PERMISSIONS.sistemas.access.key, PERMISSIONS.sistemas.borradorIaRead.key],
        },
        loadComponent: () =>
          import('@domains/sistema/feature/borrador-ia/dashboard/borrador-ia-dashboard-page.component').then(
            (m) => m.BorradorIaDashboardPageComponent,
          ),
        title: 'Dashboard Borrador IA — TCG Factory ERP',
      },
      {
        // Sistema · Gestión de Idiomas: la maestra `Idioma` como fuente única de verdad de la
        // correlación idioma ERP ↔ PrestaShop ↔ frontend. Ruta real; antes del catch-all del
        // prototipo.
        path: 'sistema/idiomas',
        canActivate: [authGuard, permissionGuard],
        data: {
          breadcrumb: 'Gestión de Idiomas',
          permission: [PERMISSIONS.sistemas.access.key, PERMISSIONS.sistemas.idiomasRead.key],
        },
        loadComponent: () =>
          import('@domains/sistema/feature/idiomas/idiomas-page.component').then(
            (m) => m.IdiomasPageComponent,
          ),
        title: 'Idiomas — TCG Factory ERP',
      },
      {
        // Sistema · Uso del buscador de tickets: monitorización del chatbot interno de búsqueda.
        // Solo lectura — los tickets se gestionan en su panel, aquí se mira al chatbot. Ruta real;
        // antes del catch-all del prototipo.
        path: 'sistema/busqueda-tickets',
        canActivate: [authGuard, permissionGuard],
        data: {
          breadcrumb: 'Uso del buscador de tickets',
          permission: [PERMISSIONS.sistemas.access.key, PERMISSIONS.sistemas.busquedaTicketsRead.key],
        },
        loadComponent: () =>
          import('@domains/sistema/feature/busqueda-chat/busqueda-chat-monitor-page.component').then(
            (m) => m.BusquedaChatMonitorPageComponent,
          ),
        title: 'Uso del buscador de tickets — TCG Factory ERP',
      },
      {
        // Sistema · Novedades: lo que trae cada subida a producción. SIN llave a propósito: el tablón
        // lo lee todo el mundo (se llega por la franja de la cabecera aunque no se vea Sistema). La
        // pestaña «Revisión» se abre dentro con `sistemas.novedades.write`.
        path: 'sistema/novedades',
        canActivate: [authGuard],
        data: { breadcrumb: 'Novedades' },
        loadComponent: () =>
          import('@domains/sistema/feature/novedades/novedades-page.component').then(
            (m) => m.NovedadesPageComponent,
          ),
        title: 'Novedades — TCG Factory ERP',
      },
      {
        // Novedades › Comunicados: alta. Sin llave en el front: quién publica (IT, Team Leaders, RRHH)
        // lo decide el backend; el formulario pregunta `puedo-publicar` y, si no, vuelve a la pestaña.
        path: 'sistema/novedades/comunicados/nuevo',
        canActivate: [authGuard],
        data: { breadcrumb: 'Nuevo comunicado' },
        loadComponent: () =>
          import('@domains/sistema/feature/novedades/comunicado-form-page.component').then(
            (m) => m.ComunicadoFormPageComponent,
          ),
        title: 'Nuevo comunicado — TCG Factory ERP',
      },
      {
        // Novedades › Comunicados: edición (mismo formulario y misma regla que el alta).
        path: 'sistema/novedades/comunicados/:id/editar',
        canActivate: [authGuard],
        data: { breadcrumb: 'Editar comunicado' },
        loadComponent: () =>
          import('@domains/sistema/feature/novedades/comunicado-form-page.component').then(
            (m) => m.ComunicadoFormPageComponent,
          ),
        title: 'Editar comunicado — TCG Factory ERP',
      },
      {
        // Sistema · Guías operativas: el contenido que usa el chatbot guía interno de Gestión de
        // Tickets. Ruta real; antes del catch-all del prototipo.
        path: 'sistema/guias-operativas',
        canActivate: [authGuard, permissionGuard],
        data: {
          breadcrumb: 'Guías',
          // IT entra por Sistema entero; los responsables de departamento, solo con la llave de Guías.
          permission: [PERMISSIONS.sistemas.access.key, PERMISSIONS.sistemas.guiasRead.key],
        },
        loadComponent: () =>
          import('@domains/sistema/feature/guias-operativas/guias-operativas-page.component').then(
            (m) => m.GuiasOperativasPageComponent,
          ),
        title: 'Guías — TCG Factory ERP',
      },
      {
        // «Guías de Tareas» se unió a Sistema › Guías: el enlace antiguo lleva allí, y el de sus
        // pares (`?seccion=preguntas`) a la pestaña de Preguntas y respuestas. Antes del catch-all.
        path: 'sistema/guias-tareas',
        redirectTo: ({ queryParams }) =>
          inject(Router).createUrlTree(['/sistema/guias-operativas'], {
            queryParams: queryParams['seccion'] === 'preguntas' ? { tab: 'preguntas' } : {},
          }),
      },
      {
        // Auditoría Borrador IA: pasó al módulo Sistema. Ruta real (el resto de Sistema
        // sigue siendo el hub de prototipo). Debe ir ANTES del catch-all del prototipo.
        path: 'sistema/borrador-ia',
        canActivate: [authGuard, permissionGuard],
        data: {
          breadcrumb: 'Auditoría Borrador IA',
          permission: [PERMISSIONS.sistemas.access.key, PERMISSIONS.sistemas.borradorIaRead.key],
        },
        loadComponent: () =>
          import('@domains/sistema/feature/borrador-ia/borrador-ia-auditoria-page.component').then(
            (m) => m.BorradorIaAuditoriaPageComponent,
          ),
        title: 'Auditoría Borrador IA — TCG Factory ERP',
      },
      {
        path: 'comunicaciones',
        loadChildren: () =>
          import('@domains/comunicaciones/comunicaciones.routes').then(
            (m) => m.comunicacionesRoutes,
          ),
      },
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      // Catálogo: hub real (graduado del prototipo). Es dueño de todo
      // `/catalogo/**`: `/catalogo` = hub de tarjetas, `/catalogo/seo` = pantalla
      // real de SEO, y el resto de submódulos = "En construcción". El breadcrumb
      // "Catálogo" enlaza al hub; ver catalogo.routes.ts.
      {
        path: 'catalogo',
        data: { breadcrumb: 'Catálogo' },
        loadChildren: () =>
          import('@domains/catalogo/catalogo.routes').then((m) => m.catalogoRoutes),
      },
      // Ventas: hub real (graduado del prototipo, igual que Catálogo). Es dueño de
      // todo `/ventas/**`: `/ventas` = hub de tarjetas, `/ventas/oportunidades`
      // = Campañas, y el resto de submódulos = "En construcción".
      {
        path: 'ventas',
        data: { breadcrumb: 'Ventas' },
        loadChildren: () => import('@domains/ventas/ventas.routes').then((m) => m.ventasRoutes),
      },
      // Almacén: hub real (graduado del prototipo, igual que Catálogo y Ventas). Es
      // dueño de todo `/almacen/**` salvo las dos pantallas de tablet de la mudanza,
      // que se montan fuera del shell (ver más abajo, antes de este bloque).
      {
        path: 'almacen',
        data: { breadcrumb: 'Almacén' },
        loadChildren: () => import('@domains/almacen/almacen.routes').then((m) => m.almacenRoutes),
      },
      // Proyectos: hub real (graduado del prototipo, igual que Ventas). Es dueño de todo
      // `/proyectos/**`: listado, maestros y el resto de tarjetas "En construcción".
      {
        path: 'proyectos',
        data: { breadcrumb: 'Proyectos' },
        loadChildren: () =>
          import('@domains/proyectos/proyectos.routes').then((m) => m.proyectosRoutes),
      },
      {
        // AlicIA vive como submódulo del hub de Sistema (/sistema/alicia). Debe ir
        // ANTES del spread del prototipo para ganar a la ruta generada `sistema`.
        path: 'sistema/alicia',
        canActivate: [authGuard, permissionGuard],
        data: {
          breadcrumb: 'AlicIA',
          permission: [PERMISSIONS.sistemas.access.key, PERMISSIONS.sistemas.aliciaRead.key],
        },
        loadChildren: () => import('@domains/alicia/alicia.routes').then((m) => m.aliciaRoutes),
      },
      {
        // Sistema · Usuarios · Roles y permisos (change roles-y-permisos). Solo IT: entra con
        // `admin.roles.read`; cambiar algo pide además `admin.roles.write` (dentro de la pantalla).
        // ANTES del spread del prototipo, que si no le generaría la página «En construcción».
        path: 'sistema/usuarios',
        canActivate: [authGuard, permissionGuard],
        data: { breadcrumb: 'Roles y permisos', permission: PERMISSIONS.admin.rolesRead.key },
        loadComponent: () =>
          import('@domains/sistema/feature/roles-permisos/roles-permisos-page.component').then(
            (m) => m.RolesPermisosPageComponent,
          ),
        title: 'Roles y permisos — TCG Factory ERP',
      },
      // Mi equipo: aprobar solicitudes, jornadas del equipo y calendario del departamento. Cada
      // pantalla lleva su key de lectura; quién ve a quién lo decide el servidor (ver mi-equipo.routes).
      // Debe ir ANTES del spread del prototipo para ganar a las páginas "En construcción".
      {
        path: 'mi-equipo',
        loadChildren: () =>
          import('@domains/mi-equipo/mi-equipo.routes').then((m) => m.miEquipoRoutes),
      },
      // Movimientos (frmGestionMovimientos): líneas de pedido de compra, venta y gastos. Se llega desde
      // las tarjetas «Ventas por producto» y «Compras por producto», y por enlace desde la ficha de
      // cliente, Gestión de productos e Info producto.
      {
        path: 'movimientos',
        data: { breadcrumb: 'Movimientos' },
        loadChildren: () =>
          import('@domains/movimientos/movimientos.routes').then((m) => m.movimientosRoutes),
      },
      // Compras: sus dos pantallas reales cuelgan de un nivel «Compras» para que las migas empiecen por
      // él (Compras › Peticiones de compra › FP-…), como Tesorería. El `canMatch` deja pasar SOLO esas dos:
      // `/compras` (el hub) y las demás tarjetas siguen siendo del prototipo (ver `compras-real.match.ts`).
      // Antes del spread del prototipo para ganarle a sus páginas «En construcción».
      {
        path: 'compras',
        canMatch: [esSubmoduloRealDeCompras],
        data: { breadcrumb: 'Compras' },
        children: [
          // «Compras por producto»: la misma pantalla de movimientos, limitada a compras y albaranes de
          // entrada (`ambito: 'compras'`).
          {
            path: 'compras-producto',
            canActivate: [authGuard, permissionGuard],
            data: {
              breadcrumb: 'Compras por producto',
              density: 'compact',
              ambito: 'compras',
              permission: PERMISSIONS.compras.comprasProductoRead.key,
            },
            loadComponent: () =>
              import('@domains/movimientos/feature/movimientos-page.component').then(
                (m) => m.MovimientosPageComponent,
              ),
            title: 'Compras por producto — TCG Factory ERP',
          },
          // «Peticiones de compra»: listado, alta y ficha. La tarjeta `peticiones` va marcada `real` en
          // `prototipo-menu.config.ts`.
          {
            path: 'peticiones',
            data: { breadcrumb: 'Peticiones de compra' },
            loadChildren: () =>
              import('@domains/compras/compras.routes').then((m) => m.peticionesCompraRoutes),
          },
        ],
      },
      // Tesorería · Cartera (cobros y pagos) del hub de Tesorería (que aún es prototipo): pantalla real y el
      // redirect de la dirección vieja `/tesoreria/cobros-pagos`. Antes del spread del prototipo. El
      // `canMatch` deja pasar SOLO esas dos direcciones: `/tesoreria` (el hub) y las demás tarjetas siguen
      // siendo del prototipo (ver `tesoreria-real.match.ts`). La tarjeta `cartera` va `real` en
      // `prototipo-menu.config.ts`.
      {
        path: 'tesoreria',
        canMatch: [esSubmoduloRealDeTesoreria],
        data: { breadcrumb: 'Tesorería' },
        loadChildren: () => import('@domains/tesoreria/tesoreria.routes').then((m) => m.tesoreriaRoutes),
      },
      // Mi cuenta: pantallas reales de vacaciones y solicitudes. Su punto de entrada es el menú
      // del avatar de la topbar, no el sidebar. Debe ir ANTES del spread del prototipo para ganar
      // a las páginas "En construcción" que genera el hub `mi-cuenta`.
      {
        path: 'mi-cuenta',
        loadChildren: () =>
          import('@domains/mi-cuenta/mi-cuenta.routes').then((m) => m.miCuentaRoutes),
      },
      // «Valor del stock» vivió en Canales de venta hasta el 16/10/2026 (change `valor-stock-a-catalogo`);
      // ahora es `/catalogo/valor-stock`. La dirección vieja redirige para no romper marcadores. Debe ir
      // ANTES del spread del prototipo, que si no la convertiría en «En construcción».
      { path: 'canales-venta/valor-stock', redirectTo: '/catalogo/valor-stock' },
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      // Prototipo de menú (en construcción): registra los hubs `ventas`,
      // `compras`, … como rutas de primer nivel. Empty-path prefix:
      // sólo entra tras descartar el redirect exacto a dashboard de arriba.
      {
        path: '',
        loadChildren: () =>
          import('@domains/prototipo/prototipo.routes').then((m) => m.prototipoRoutes),
      },
    ],
  },

  // ── Styleguide (solo desarrollo) ────────────────────────────────────────
  {
    path: 'styleguide',
    loadComponent: () =>
      import('@domains/styleguide/styleguide-shell.component').then(
        (m) => m.StyleguideShellComponent,
      ),
    canActivate: [devGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'identity' },
      {
        path: 'identity',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-identity/sg-identity.component').then(
            (m) => m.SgIdentityComponent,
          ),
        title: 'Identidad — Styleguide',
      },
      {
        path: 'colors',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-colors/sg-colors.component').then(
            (m) => m.SgColorsComponent,
          ),
        title: 'Color — Styleguide',
      },
      {
        path: 'typography',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-typography/sg-typography.component').then(
            (m) => m.SgTypographyComponent,
          ),
        title: 'Tipografía — Styleguide',
      },
      {
        path: 'spacing',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-spacing/sg-spacing.component').then(
            (m) => m.SgSpacingComponent,
          ),
        title: 'Espaciado — Styleguide',
      },
      {
        path: 'icons',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-icons/sg-icons.component').then(
            (m) => m.SgIconsComponent,
          ),
        title: 'Iconografía — Styleguide',
      },
      {
        path: 'ux-kit',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-ux-kit/sg-ux-kit.component').then(
            (m) => m.SgUxKitComponent,
          ),
        title: 'UX Kit — Styleguide',
      },
      {
        path: 'primeng',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-primeng/sg-primeng.component').then(
            (m) => m.SgPrimeNgComponent,
          ),
        title: 'PrimeNG — Styleguide',
      },
      {
        path: 'tablas-maestras',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-tablas-maestras/sg-tablas-maestras.component').then(
            (m) => m.SgTablasMaestrasComponent,
          ),
        title: 'Tablas maestras — Styleguide',
      },
      {
        path: 'voice',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-voice/sg-voice.component').then(
            (m) => m.SgVoiceComponent,
          ),
        title: 'Voz y Tono — Styleguide',
      },
      {
        path: 'templates',
        loadComponent: () =>
          import('@domains/styleguide/sections/sg-templates/sg-templates.component').then(
            (m) => m.SgTemplatesComponent,
          ),
        title: 'Plantillas — Styleguide',
      },
    ],
  },

  // ── Fallback ─────────────────────────────────────────────────────────────
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
